import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ReactComponent as CloseIcon } from '../../assets/icons/close-circle.svg';
import { ReactComponent as DownArrowIcon } from '../../assets/icons/down-arrow.svg';
import { ReactComponent as EditIcon } from '../../assets/icons/edit.svg';
import { ReactComponent as ChargersIcon } from '../../assets/icons/sidebar/sidebar-chargers.svg';
import { ReactComponent as CPOIcon } from '../../assets/icons/sidebar/sidebar-cpo.svg';
import { ReactComponent as Mobility } from '../../assets/icons/sidebar/sidebar-emsp.svg';
import { ReactComponent as UsersActiveIcon } from '../../assets/icons/sidebar/sidebar-users.svg';
import { ReactComponent as ExpandIcon } from '../../assets/icons/tables/expand-icon.svg';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import { fetchAllPermissions, fetchAllRoles } from '../../redux/slices/RolesManagementSlice';
import { changeDateFormat } from '../../shared-functions/DateTime';
import { permissionCheck } from '../../shared-functions/JWTDocodeFunction';
import { stringValidation } from '../../shared-functions/Validation';
import roles from '../../utils/api/roles';
import MyButton from '../button/Button';
import CheckBoxButtons from '../check-box-buttons/CheckBoxButtons';
import CustomCheckbox from '../custom-check-box/CustomCheckBox';
import CustomInput from '../custom-input/CustomInput';
import ModalBox from '../modal-box/ModalBox';

interface RoleSidePanelProperties {
  fetchIdFrom: 'queryParams' | 'props';
  id?: string;
  onEdit?: (id: string) => void;
  onClose?: () => void;
}

interface RoleDataProperties {
  _id: string;
  id: string;
  name: string;
  responsibility: string;
  permissions: string[];
  createdAt?: string;
  users: {
    name: string;
    status: string;
    email: string;
    phoneNumber: string;
  }[];
}

interface RoleFormState {
  name: string;
  responsibility: string;
  permissions: (string | number)[];
}

/**
 * Role side panel
 * @param {any} properties Component properties
 * @returns {any} role side panel
 */
function RoleSidePanel(properties: RoleSidePanelProperties) {
  const { t } = useTranslation('userRole');
  const navigate = useNavigate();
  const { fetchIdFrom, id, onClose } = properties;

  const dispatch = useAppDispatch();
  const { permissionsList, page, limit } = useAppSelector((state) => state.roles);
  const [searchParameters] = useSearchParams();

  const [roleData, setRoleData] = useState<RoleDataProperties>();

  // Edit Mode State
  const [editMode, setEditMode] = useState(false);
  const [roleDetails, setRoleDetails] = useState<RoleFormState>({
    name: '',
    responsibility: '',
    permissions: [],
  });
  const [showFormErrors, setShowFormErrors] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  const [modalView, setModalView] = useState({
    status: false,
    title: '',
    content: <div />,
    header: false,
    rightCorner: '',
    notification: false,
  });

  const [expandedModules, setExpandedModules] = useState<string[]>([]);

  /**
   * toggleExpand
   * @param {any} moduleKey - The moduleKey
   */
  const toggleExpand = (moduleKey: string) => {
    setExpandedModules((previous) =>
      previous.includes(moduleKey)
        ? previous.filter((item) => item !== moduleKey)
        : [...previous, moduleKey],
    );
  };
 
  /**
   * Function to fetch role details
   * @param {any} roleId role ID
   */
  const handleFetchRoleDetails = async (roleId: string) => {
    roles
      .fetchRoleById(roleId)
      .then((response) => {
        setRoleData(response?.data);
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            status: true,
            message: error?.response?.data?.message || t('somethingWentWrong'),
            type: 'error',
            code: 0,
          }), 
        );
      });
  };

  /**
   * Toggle Edit Mode
   */
  const toggleEditMode = () => {
    navigate(`../role/edit/${searchParameters.get('roleId') || id}`, { state: { ...roleData } });
  };

  /**
   * Handle Input Change
   * @param {any} name - The name
   * @param {any} value - The value
   */
  const handleInputChange = (name: string, value: string | number | (string | number)[]) => {
    setRoleDetails((previous) => ({
      ...previous,
      [name]: value,
    }));
  };
  /**
   * Handle Edit Submit
   */
  const handleEditSubmit = () => {
    setShowFormErrors(true);
    if (!roleData) return;

    if (
      stringValidation(1, 100, roleDetails.name) &&
      stringValidation(1, 100, roleDetails.responsibility) &&
      roleDetails.permissions.length > 0
    ) {
      setFormLoading(true);
      const request = {
        name: roleDetails.name,
        responsibility: roleDetails.responsibility,
        permissions: roleDetails.permissions,
      };
      roles
        .editRoles(roleData._id, request)
        .then(() => {
          setFormLoading(false);
          setEditMode(false);
          dispatch(
            setNotificationMessage({
              message: t('roleUpdatedSuccessfully') || 'Role updated successfully',
              status: true,
              type: 'success',
              code: 0,
            }),
          );
          // Refresh details and list
          handleFetchRoleDetails(roleData._id);
          dispatch(fetchAllRoles({ page, limit }));
        })
        .catch((error) => {
          setFormLoading(false);
          dispatch(
            setNotificationMessage({
              message: error?.response?.data?.message,
              status: true,
              type: 'error',
              code: error?.response?.data?.code,
            }),
          );
        });
    }
  };

  useEffect(() => {
    let roleId;
    if (fetchIdFrom === 'queryParams') {
      roleId = searchParameters.get('roleId');
    } else if (fetchIdFrom === 'props') {
      roleId = id;
    }

    if (!roleId) {
      setRoleData(undefined);
      setEditMode(false);
      return;
    }

    handleFetchRoleDetails(roleId);
    setEditMode(false); // Reset edit mode on ID change
  }, [searchParameters, id]);

  useEffect(() => {
    if (permissionsList.length === 0) {
      dispatch(fetchAllPermissions());
    }
  }, []);

  return (
    <div className="user-details-side-panel role-side-panel">
      <ModalBox
        open={modalView.status}
        title={modalView.title}
        content={modalView.content}
        closeModal={() => setModalView((previous) => ({ ...previous, status: false }))}
        header={modalView.header}
        rightCorner={modalView.rightCorner}
        notification={modalView.notification}
      />

      {fetchIdFrom === 'props' && (
        <div className="side-panel-header-component">
          <div className="side-panel-header-left-title-actions">
            <p className="side-panel-header-title">
              {editMode ? t('editRole') || 'Edit Role' : t('roleDetails')}
            </p>
            <div className="side-panel-header-left-icons">
              {!editMode && (
                <>
                  {/* Expand Pages */}
                  <div className="button-wrap">
                    <span
                      role="presentation"
                      className="access-facility"
                      onClick={() => {
                        navigate(`../role/edit/${roleData?._id || id}`, { state: { ...roleData } });
                      }}>
                      <ExpandIcon className="icon" />
                    </span>
                    <div className="hover-label">{t('expandPages') || 'Expand Pages'}</div>
                  </div>

                  {/* Edit Icon */}
                  {permissionCheck('userRoleManagementUpdate') && (
                    <div className="button-wrap">
                      <span
                        role="presentation"
                        className="access-facility"
                        onClick={toggleEditMode}>
                        <EditIcon className="icon" />
                      </span>
                      <div className="hover-label">{t('edit') || 'Edit'}</div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          <CloseIcon className="icon close-btn" onClick={onClose} />
        </div>
      )}

      <div className="scrollable-content">
        <div className="user-details-group">
          <div className="profile-card">
            {editMode ? (
              // Edit Mode Fields
              <div className="edit-fields-container">
                <div className="info-row-input">
                  <CustomInput
                    name="name"
                    inputLabel={t('roleName')}
                    type="text"
                    placeholder={t('enterName')}
                    value={roleDetails.name}
                    onChangeValue={handleInputChange}
                    valueRequired
                    error={showFormErrors && !stringValidation(1, 100, roleDetails.name)}
                    errorMessage={t('roleNameRequired') || 'Role name is required'}
                  />
                </div>

                <div className="info-row-input">
                  <CustomInput
                    name="responsibility"
                    inputLabel={t('responsibility')}
                    type="text"
                    placeholder={t('describeHere')}
                    value={roleDetails.responsibility}
                    onChangeValue={handleInputChange}
                    valueRequired
                    error={showFormErrors && !stringValidation(1, 100, roleDetails.responsibility)}
                    errorMessage={t('responsibilityRequired') || 'Responsibility is required'}
                  />
                </div>

                <div className="info-row-input">
                  <CheckBoxButtons
                    name="permissions"
                    list={permissionsList}
                    inputLabel={t('permissions')}
                    value={roleDetails.permissions}
                    onChangeValue={handleInputChange}
                    valueRequired
                    error={showFormErrors && roleDetails.permissions.length === 0}
                    errorMessage={t('permissionsRequired') || 'At least one permission is required'}
                  />
                </div>

                <div className="footer-actions">
                  <MyButton label={t('cancel')} buttonType="outline" onClickFunc={toggleEditMode} />
                  <MyButton
                    label={t('update')}
                    buttonType="submit"
                    onClickFunc={handleEditSubmit}
                    disableButton={formLoading}
                    loading={formLoading}
                  />
                </div>
              </div>
            ) : (
              // View Mode Layout
              <div className="edit-fields-container">
                <div className="info-row-input info-row-flex">
                  <CustomInput
                    name="name"
                    inputLabel={t('roleName')}
                    type="text"
                    placeholder={t('enterName')}
                    value={roleData?.name || ''}
                    onChangeValue={() => {}}
                    valueRequired
                    readOnly
                    fontSize="small"
                  />
                  <CustomInput
                    name="createdAt"
                    inputLabel={t('createdAt')}
                    type="text"
                    placeholder=""
                    value={roleData?.createdAt ? changeDateFormat(roleData.createdAt) : '-'}
                    onChangeValue={() => {}}
                    readOnly
                    fontSize="small"
                  />
                </div>

                <div className="info-row-input">
                  <CustomInput
                    name="responsibility"
                    inputLabel={t('responsibility')}
                    type="text"
                    placeholder={t('describeHere')}
                    value={roleData?.responsibility || ''}
                    onChangeValue={() => {}}
                    valueRequired
                    readOnly
                  />
                </div>

                <div className="permissions-section">
                  <div className="permissions-header">
                    <h4>{t('permissions')}</h4>
                  </div>

                  {permissionsList.map((group: any) => {
                    const groupSelectedCount = group.permissions.reduce(
                      (count: number, module: any) => {
                        return (
                          count +
                          module.features.filter((perm: any) =>
                            roleData?.permissions?.includes(perm.permission),
                          ).length
                        );
                      },
                      0,
                    );
                    if (groupSelectedCount === 0) {
                      return null;
                    }
                    return (
                      <div key={group.key} className="permission-group-card">
                        <div
                          className="permission-group-header"
                          onClick={() => toggleExpand(group.key)}
                          role="presentation">
                          <div className="group-title-wrapper">
                            <span className="group-icon-wrapper">
                              {group.name.includes('CPO') && <CPOIcon />}
                              {group.name.includes('Mobility') && <Mobility />}
                              {group.name.includes('Devices') && <ChargersIcon />}
                              {group.name.includes('User') && <UsersActiveIcon />}
                            </span>
                            <p className="heading-module-types">{group.name}</p>
                          </div>

                          <div className="group-actions-wrapper">
                            <div className="permission-count-pill">{groupSelectedCount}</div>

                            <button
                              type="button"
                              className={`expand-btn ${expandedModules.includes(group.key) ? 'open' : ''}`}
                              aria-label="Toggle permissions">
                              <DownArrowIcon />
                            </button>
                          </div>
                        </div>

                        {expandedModules.includes(group.key) && (
                          <div className="group-module-list">
                            {group.permissions.map((module: any) => {
                              // const moduleSelected = module.features.every((permission: any) =>
                              //   roleData?.permissions?.includes(permission.permission),
                              // );
                              return (
                                <div key={module.key} className="module-card">
                                  <div
                                    className="module-header"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      toggleExpand(module.key);
                                    }}
                                    role="presentation">
                                    <span className="module-title">{module.name}</span>

                                    <div className="module-header-right">
                                      <button
                                        type="button"
                                        className={`expand-btn ${expandedModules.includes(module.key) ? 'open' : ''}`}
                                        aria-label="Toggle permissions">
                                        <DownArrowIcon />
                                      </button>
                                    </div>
                                  </div>

                                  {expandedModules.includes(module.key) && (
                                    <div className="permission-list">
                                      {module.features.map((permission: any) => {
                                        return (
                                          <CustomCheckbox
                                            key={permission.key}
                                            name={permission.permission}
                                            value={
                                              roleData?.permissions?.includes(
                                                permission.permission,
                                              ) || false
                                            }
                                            checkboxLabel={permission.name}
                                            onChangeValue={() => {}}
                                          />
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default RoleSidePanel;
